import { useState } from 'react';
import { useApi } from '../lib/use-api';
import { api, money, shortDate } from '../lib/api';
import { Card, Empty, ErrorNotice, Loading, Stat } from '../components/ui';

interface Invoice {
  id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total: string;
  currency: string;
  status: string;
}

interface Device {
  deviceId: string;
  locationName: string;
}

/** Billing, invoices and warranty (FR-B14, FR-B15). */
export function Billing() {
  const invoices = useApi<{
    combinedBilling: boolean;
    summary: { outstanding: number; currency: string; count: number };
    items: Invoice[];
  }>('/billing/invoices');
  const devices = useApi<{ items: Device[] }>('/devices');

  const [deviceId, setDeviceId] = useState('');
  const [term, setTerm] = useState('24');
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function extend() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.post<{ warranty: { termMonths: number; expiresAt: string | null } }>(
        '/warranty/extend',
        { deviceId, newTermMonths: Number(term) },
      );
      setMessage(
        `Warranty extended to ${result.warranty.termMonths} months, covering until ${shortDate(
          result.warranty.expiresAt,
        )}.`,
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (invoices.loading) return <Loading />;

  return (
    <>
      <div className="page-head">
        <h1>Billing</h1>
        <p>{invoices.data?.combinedBilling ? 'Combined billing across your locations.' : 'Invoices and warranty.'}</p>
      </div>

      <ErrorNotice error={error ?? invoices.error} />
      {message ? <div className="notice ok">{message}</div> : null}

      <div className="stats">
        <Stat
          value={money(invoices.data?.summary.outstanding, invoices.data?.summary.currency)}
          label="Outstanding"
        />
        <Stat value={invoices.data?.summary.count ?? 0} label="Invoices" />
      </div>

      <Card title="Invoices">
        {invoices.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Period</th>
                <th className="num">Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.data.items.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="mono">{invoice.invoice_number}</td>
                  <td className="muted">
                    {shortDate(invoice.period_start)} – {shortDate(invoice.period_end)}
                  </td>
                  <td className="num">{money(invoice.total, invoice.currency)}</td>
                  <td>
                    <span className={`badge ${invoice.status === 'paid' ? 'ok' : invoice.status === 'overdue' ? 'danger' : ''}`}>
                      {invoice.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty title="No invoices yet">
            <p className="muted">Invoices appear once your venue starts collecting guest revenue.</p>
          </Empty>
        )}
      </Card>

      <Card title="Extend warranty">
        <div className="card-body">
          <p className="muted" style={{ marginTop: 0 }}>
            Extend cover to 2 or 3 years. This must be bought before your current warranty expires.
          </p>
          <div className="row">
            <div className="field">
              <label htmlFor="device">Device</label>
              <select id="device" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                <option value="">Choose a device…</option>
                {(devices.data?.items ?? []).map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.locationName} ({device.deviceId})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="term">New total term</label>
              <select id="term" value={term} onChange={(e) => setTerm(e.target.value)}>
                <option value="24">2 years</option>
                <option value="36">3 years</option>
              </select>
            </div>
          </div>
          <button disabled={busy || !deviceId} onClick={extend}>
            {busy ? 'Processing…' : 'Purchase extension'}
          </button>
        </div>
      </Card>
    </>
  );
}
